import { useAuth0 } from "@auth0/auth0-react";
import { yupResolver } from "@hookform/resolvers/yup";
import { Box } from "@mui/material";
import { PropTypes } from "prop-types";
import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

import { AutoCompleteMenu, FileLink, Loader, UploadInput } from "@/atoms";
import { ContributorsFields, FormContainer } from "@/components";
import {
    actionsData,
    fileData,
    identifiersData,
    inputData,
    permissionsData,
    selectionsData,
    trackFormData,
} from "@/data";
import {
    useArtistRolesList,
    useArtistsList,
    useContributorsList,
    useTrackFormatsSelectionList,
    useUploadFile,
} from "@/hooks";
import { useTrackSchema } from "@/schemas";
import { StyledTextField } from "@/styles";
import { canDo, deleteFileHandler, renderFormControllerHandler, setUploadInputPreviousValueHandler } from "@/utils";

const TrackForm = ({ action, hasDrawer, loading, onSubmitForm, values }) => {
    const { t } = useTranslation();
    const { user } = useAuth0();
    const selections = useSelector((state) => state.selectionsReducer);

    const initialFields = values?.trackContributorRoles
        ? values.trackContributorRoles.map((item, index) => ({
              _cleared: false,
              _isNew: false,
              contributorIds: item.contributors?.map((c) => c.value) || [],
              roleId: item?.role?.value || "",
              uniqueId: `${Date.now()}_${index}`,
          }))
        : [
              {
                  _cleared: false,
                  _isNew: true,
                  contributorIds: [],
                  roleId: "",
                  uniqueId: `${Date.now()}_0`,
              },
          ];

    const [fields, setFields] = useState(initialFields);
    const [finalizedFieldsArray, setFinalizedFieldsArray] = useState([]);

    const { filesData, filesLoading, setFilesData, uploadFile } = useUploadFile();
    const { loading: tracksFormatsLoading } = useTrackFormatsSelectionList();

    const {
        count: artistsCount,
        fetched: artistsFetched,
        loading: artistsLoading,
        page: artistsPage,
        refetch: artistsRefetch,
        searchKey: artistsSearchKey,
    } = useArtistsList(true, true, {}, null, null, true);

    const {
        count: artistRolesCount,
        loading: artistRolesLoading,
        nextCursor: artistRolesNextCursor,
        page: artistRolesPage,
        refetch: artistRolesRefetch,
        searchKey: artistRolesSearchKey,
    } = useArtistRolesList(false, true);

    const {
        count: contributorsCount,
        data: contributorsData,
        loading: contributorsLoading,
        page: contributorsPage,
        refetch: contributorsRefetch,
        searchKey: contributorsSearchKey,
    } = useContributorsList(true, true);

    const asyncMenus = useMemo(
        () => ({
            artists: {
                count: artistsCount,
                loading: artistsLoading,
                page: artistsPage,
                refetch: artistsRefetch,
                searchKey: artistsSearchKey,
                selectionKey: selectionsData.keys.artists,
            },
            contributors: {
                count: contributorsCount,
                data: contributorsData,
                loading: contributorsLoading,
                page: contributorsPage,
                refetch: contributorsRefetch || (() => {}),
                searchKey: contributorsSearchKey,
                selectionKey: selectionsData.keys.contributors,
            },
            roles: {
                count: artistRolesCount,
                loading: artistRolesLoading,
                nextCursor: artistRolesNextCursor,
                page: artistRolesPage,
                refetch: artistRolesRefetch || (() => {}),
                searchKey: artistRolesSearchKey,
                selectionKey: selectionsData.keys.roleIds,
            },
        }),
        [
            artistsCount,
            artistsLoading,
            artistsPage,
            artistsRefetch,
            artistsSearchKey,
            artistRolesCount,
            artistRolesLoading,
            artistRolesNextCursor,
            artistRolesPage,
            artistRolesRefetch,
            artistRolesSearchKey,
            contributorsCount,
            contributorsData,
            contributorsLoading,
            contributorsPage,
            contributorsRefetch,
            contributorsSearchKey,
        ],
    );

    const { mediaCreate: mediaCreatePermission, mediaRead: mediaReadPermission } = permissionsData.permissions.keys;
    const allowedPermissions = [mediaCreatePermission, mediaReadPermission];

    const {
        oldImage: oldImageInputName,
        oldTrack: oldTrackInputName,
        originalTrack: originalTrackInputName,
    } = inputData.names;

    const {
        autoComplete: autoCompleteInputType,
        file: fileInputType,
        multiAutoComplete: multiAutoCompleteInputType,
        text: textInputType,
        textArea: textAreaInputType,
    } = inputData.types;

    const schema = useTrackSchema();
    const formMethods = useForm({
        defaultValues: values,
        mode: "onBlur",
        resolver: yupResolver(schema),
    });

    const {
        control,
        formState: { errors },
        setError,
        setValue,
        watch,
    } = formMethods;

    const trackFormat = watch("trackFormat");

    const isStereo = useMemo(
        () =>
            trackFormat &&
            trackFormat === selections.trackFormats?.find((format) => format.label === "audio.2_0")?.value,
        [trackFormat, selections.trackFormats],
    );

    const filteredFields = useMemo(() => {
        if (isStereo || !trackFormat) {
            return trackFormData.filter((field) => field.name !== originalTrackInputName);
        }
        return trackFormData;
    }, [isStereo, trackFormat, originalTrackInputName]);

    useEffect(() => {
        setValue("hasOriginalTrack", !isStereo);

        setFinalizedFieldsArray(filteredFields);
    }, [setValue, isStereo, filteredFields]);

    useEffect(() => {
        if (isStereo) {
            setValue("originalTrack", null);
        }
    }, [isStereo, setValue]);

    useEffect(() => {
        setUploadInputPreviousValueHandler(watch, setError, setValue, filesData, fileData.names.track);
    }, [filesData, setError, setValue, watch]);

    useEffect(() => {
        setUploadInputPreviousValueHandler(watch, setError, setValue, filesData, fileData.names.image);
    }, [filesData, setError, setValue, watch]);

    const renderFormControllerChildrenHandler = (field, label, name, type, isOptional, selectionKey) => {
        switch (type) {
            case textInputType:
            case textAreaInputType:
                return (
                    <StyledTextField
                        {...field}
                        error={errors[name]}
                        helperText={errors?.[name]?.message}
                        id={`${name}_${identifiersData.ids.input}`}
                        minRows={type === textAreaInputType ? 4 : 1}
                        multiline={type === textAreaInputType}
                        value={field.value || ""}
                        variant="outlined"
                        label={isOptional ? `${t(`labels.${label}`)} (${t("labels.optional")})` : t(`labels.${label}`)}
                        fullWidth
                    />
                );
            case fileInputType: {
                const file = name === fileData.names.image ? oldImageInputName : oldTrackInputName;
                return (
                    <Box
                        sx={{
                            mb: 1,
                        }}
                    >
                        <UploadInput
                            color={errors[name]?.message ? "error" : "primary"}
                            error={errors[name]?.message}
                            field={field}
                            hasError={!!errors[name]?.message}
                            id={name}
                            label={t(`labels.${label}`)}
                            loading={filesLoading?.[name]}
                            accept={
                                name === fileData.names.image
                                    ? fileData.acceptedExtensions.image
                                    : fileData.acceptedExtensions.audio
                            }
                            disabled={!canDo(user, allowedPermissions)}
                            onChange={(e) => uploadFile(e.target.files[0], formMethods, name)}
                        />
                        {filesData?.[name] && (
                            <FileLink
                                id={name}
                                name={filesData?.[name]?.name}
                                deleteAction={() => deleteFileHandler(setFilesData, formMethods, name)}
                                hasDelete
                            />
                        )}
                        {(values?.oldImage || values?.oldTrack) && !filesData?.[name] && (
                            <FileLink
                                hasDownload={!!values?.[file]}
                                id={name}
                                hasImage={!!(name === fileData.names.image && values?.oldImage?.downloadThumbnail)}
                                link={
                                    name === fileData.names.image ? values?.[file]?.downloadThumbnail : values?.[file]
                                }
                                name={
                                    name === fileData.names.image ? values?.[file]?.downloadThumbnail : values?.[file]
                                }
                                hasJustifyStart
                            />
                        )}
                    </Box>
                );
            }
            case multiAutoCompleteInputType:
                return (
                    <AutoCompleteMenu
                        asyncMenuData={asyncMenus?.[selectionKey]}
                        defaultValue={values ? values[`${name}Array`] : []}
                        disabledOption={watch(inputData.names.primaryArtist)}
                        errors={errors}
                        getRenderedOption={(option) => option?.label}
                        isOptional={isOptional}
                        label={label}
                        name={name}
                        optionLabel={(option) => option.label}
                        options={selections?.[selectionKey]}
                        isAsyncMenu
                        isMultiple
                        onChange={(_, options) => field.onChange(options || [])}
                    />
                );
            case autoCompleteInputType:
                return (
                    <AutoCompleteMenu
                        asyncMenuData={asyncMenus?.[selectionKey]}
                        defaultValue={values?.[`${name}Obj`]}
                        disabled={name === "trackFormat" && values?.trackFormat}
                        errors={errors}
                        getRenderedOption={(option) => option?.label}
                        label={label}
                        name={name}
                        optionLabel={(option) => option.label}
                        options={selections?.[selectionKey]}
                        isAsyncMenu
                        onChange={(_, option) => field.onChange(option?.value || "")}
                    />
                );
            default:
                return "";
        }
    };

    if ((artistsLoading && !artistsFetched) || tracksFormatsLoading || !selections.hasLoaded.artists) return <Loader />;

    return (
        <FormContainer
            action={action}
            disabled={filesLoading?.imageFile || filesLoading?.trackFile}
            filesData={[fileData.names.image, fileData.names.track]}
            formMethods={formMethods}
            hasClearAfterSubmission={action === actionsData.create}
            loading={loading}
            setFilesData={setFilesData}
            data={renderFormControllerHandler(
                control,
                finalizedFieldsArray,
                renderFormControllerChildrenHandler,
                hasDrawer,
            )}
            hasSubmitButton={canDo(user, allowedPermissions)}
            otherData={
                <ContributorsFields
                    asyncMenus={asyncMenus}
                    fields={fields}
                    isDrawer={hasDrawer}
                    setFields={setFields}
                    setValue={setValue}
                    values={values}
                />
            }
            withoutShadow
            onSubmitForm={onSubmitForm}
        />
    );
};

export default TrackForm;

TrackForm.propTypes = {
    action: PropTypes.string,
    hasDrawer: PropTypes.bool,
    loading: PropTypes.bool,
    onSubmitForm: PropTypes.func,
    values: PropTypes.object,
};
